import { useState, useEffect, useRef, useMemo } from "react";
import LeadGateModal from "../Modals/LeadGateModal.jsx";
import SuccessModal from "../Modals/SuccessModal.jsx";
import ConfirmSendResultsModal from "../Modals/ConfirmSendResultsModal.jsx";
import UpdateEmailModal from "../Modals/UpdateEmailModal.jsx";

import { detectToolsOrigin } from "../../../utils/helper.js";
import axios from "axios";
import "../calculator.scss";

// ===== YEAR CONFIG: DISCLAIMER (same pattern as others) =====
const YEAR_CONFIG = {
  2025: {
    disclaimer:
      "* This calculator provides estimates based on 2025 federal tax brackets. Actual tax liability may vary based on deductions, credits, state taxes, and other factors. Consult a tax professional for personalized advice.",
  },
  2026: {
    disclaimer:
      "* This calculator provides estimates based on 2026 federal tax brackets. Actual tax liability may vary based on deductions, credits, state taxes, and other factors. Consult a tax professional for personalized advice.",
  },
};

// ✅ FIX: one source of truth for statuses (key + label)
const FILING_STATUSES = [
  { key: "single", label: "Single" },
  { key: "marriedJointly", label: "Married Filing Jointly" },
  { key: "marriedSeparately", label: "Married Filing Separately" },
  { key: "headOfHousehold", label: "Head of Household" },
];

// ---- STANDARD DEDUCTIONS BY YEAR ----
const STANDARD_DEDUCTIONS_BY_YEAR = {
  2025: {
    single: 15000,
    marriedJointly: 30000,
    marriedSeparately: 15000,
    headOfHousehold: 22500,
  },
  2026: {
    single: 16100,
    marriedJointly: 32200,
    marriedSeparately: 16100,
    headOfHousehold: 24150,
  },
};

// ---- TAX BRACKETS BY YEAR ----
const TAX_BRACKETS_BY_YEAR = {
  2025: {
    single: [
      { rate: 0.1, min: 0, max: 11925 },
      { rate: 0.12, min: 11925, max: 48475 },
      { rate: 0.22, min: 48475, max: 103350 },
      { rate: 0.24, min: 103350, max: 197300 },
      { rate: 0.32, min: 197300, max: 250525 },
      { rate: 0.35, min: 250525, max: 626350 },
      { rate: 0.37, min: 626350, max: Infinity },
    ],
    marriedJointly: [
      { rate: 0.1, min: 0, max: 23850 },
      { rate: 0.12, min: 23850, max: 96950 },
      { rate: 0.22, min: 96950, max: 206700 },
      { rate: 0.24, min: 206700, max: 394600 },
      { rate: 0.32, min: 394600, max: 501050 },
      { rate: 0.35, min: 501050, max: 751600 },
      { rate: 0.37, min: 751600, max: Infinity },
    ],
    marriedSeparately: [
      { rate: 0.1, min: 0, max: 11925 },
      { rate: 0.12, min: 11925, max: 48475 },
      { rate: 0.22, min: 48475, max: 103350 },
      { rate: 0.24, min: 103350, max: 197300 },
      { rate: 0.32, min: 197300, max: 250525 },
      { rate: 0.35, min: 250525, max: 375800 },
      { rate: 0.37, min: 375800, max: Infinity },
    ],
    headOfHousehold: [
      { rate: 0.1, min: 0, max: 17000 },
      { rate: 0.12, min: 17000, max: 64850 },
      { rate: 0.22, min: 64850, max: 103350 },
      { rate: 0.24, min: 103350, max: 197300 },
      { rate: 0.32, min: 197300, max: 250500 },
      { rate: 0.35, min: 250500, max: 626350 },
      { rate: 0.37, min: 626350, max: Infinity },
    ],
  },
  2026: {
    single: [
      { rate: 0.1, min: 0, max: 12400 },
      { rate: 0.12, min: 12401, max: 50400 },
      { rate: 0.22, min: 50401, max: 105700 },
      { rate: 0.24, min: 105701, max: 201775 },
      { rate: 0.32, min: 201776, max: 256225 },
      { rate: 0.35, min: 256226, max: 640600 },
      { rate: 0.37, min: 640601, max: Infinity },
    ],
    marriedJointly: [
      { rate: 0.1, min: 0, max: 24800 },
      { rate: 0.12, min: 24801, max: 100800 },
      { rate: 0.22, min: 100801, max: 211400 },
      { rate: 0.24, min: 211401, max: 403550 },
      { rate: 0.32, min: 403551, max: 512450 },
      { rate: 0.35, min: 512451, max: 768700 },
      { rate: 0.37, min: 768701, max: Infinity },
    ],
    marriedSeparately: [
      { rate: 0.1, min: 0, max: 12400 },
      { rate: 0.12, min: 12401, max: 50400 },
      { rate: 0.22, min: 50401, max: 105700 },
      { rate: 0.24, min: 105701, max: 201775 },
      { rate: 0.32, min: 201776, max: 256225 },
      { rate: 0.35, min: 256226, max: 384350 },
      { rate: 0.37, min: 384351, max: Infinity },
    ],
    headOfHousehold: [
      { rate: 0.1, min: 0, max: 17700 },
      { rate: 0.12, min: 17701, max: 67450 },
      { rate: 0.22, min: 67451, max: 105700 },
      { rate: 0.24, min: 105701, max: 201750 },
      { rate: 0.32, min: 201751, max: 256200 },
      { rate: 0.35, min: 256201, max: 640600 },
      { rate: 0.37, min: 640601, max: Infinity },
    ],
  },
};

function calculateTax(agi, filingStatusKey, year) {
  const stdMap =
    STANDARD_DEDUCTIONS_BY_YEAR[year] || STANDARD_DEDUCTIONS_BY_YEAR[2025];
  const bracketsMap = TAX_BRACKETS_BY_YEAR[year] || TAX_BRACKETS_BY_YEAR[2025];

  const deduction = stdMap[filingStatusKey] ?? 0;
  const taxableIncome = Math.max(0, agi - deduction);
  const brackets = bracketsMap[filingStatusKey] || [];

  let totalTax = 0;
  const breakdown = [];

  for (const bracket of brackets) {
    if (taxableIncome > bracket.min) {
      const incomeInBracket = Math.max(
        0,
        Math.min(taxableIncome, bracket.max) - bracket.min
      );
      if (incomeInBracket > 0) {
        const taxInBracket = incomeInBracket * bracket.rate;
        totalTax += taxInBracket;
        breakdown.push({
          rate: bracket.rate,
          min: bracket.min,
          max: bracket.max,
          taxableAmount: incomeInBracket,
          tax: taxInBracket,
        });
      }
    }
  }

  const marginalRate =
    (brackets.find((b) => taxableIncome <= b.max) ||
      brackets[brackets.length - 1] ||
      { rate: 0 }).rate;

  const effectiveRate = agi > 0 ? totalTax / agi : 0;

  return {
    totalTax,
    marginalRate,
    effectiveRate,
    breakdown,
  };
}

// ---------- PROXY (same pattern as Federal) ----------
const PROXY_URL =
  window.__UK_PROXY_URL__ || `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

function TaxBracketCalculator({
  year = 2025,
  calculatorName = "Tax Bracket Calculator",
  endpoint = PROXY_URL,
}) {
  const yearCfg = YEAR_CONFIG[year] || YEAR_CONFIG[2025];

  // ✅ FIX: keep key + label separately
  const [filingStatusKey, setFilingStatusKey] = useState("single");

  const filingStatusLabel =
    FILING_STATUSES.find((s) => s.key === filingStatusKey)?.label || "";

  const [agi, setAgi] = useState("");
  const [results, setResults] = useState(null);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(() => params.has("qa_mode"), [params]);

  // ✅ keep latest payload for email sending (add pdf_notes like others)
  const latestPayloadRef = useRef({
    inputs_json: null,
    results_json: null,
    pdf_notes: null,
  });

  const resultsRef = useRef(null);

  // ---------- Gating storage helpers (24h, shared across calculators) ----------
  const UNK_GATE_KEY = "unk_calc_unlock_v1";
  const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;

  const readUnlock = () => {
    try {
      const raw = localStorage.getItem(UNK_GATE_KEY);
      if (!raw) return { unlocked: false, email: "" };
      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt || 0);
      const email = String(parsed?.email || "");
      if (!expiresAt || Date.now() > expiresAt) {
        localStorage.removeItem(UNK_GATE_KEY);
        return { unlocked: false, email: "" };
      }
      return { unlocked: true, email };
    } catch {
      return { unlocked: false, email: "" };
    }
  };

  const writeUnlock = ({ email }) => {
    const payload = {
      expiresAt: Date.now() + UNK_GATE_TTL_MS,
      email: String(email || ""),
    };
    localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
  };

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  const [unlocked, setUnlocked] = useState(false);
  const [storedEmail, setStoredEmail] = useState("");

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const [sendingResults, setSendingResults] = useState(false);

  useEffect(() => {
    const gate = readUnlock();
    setUnlocked(gate.unlocked);
    setStoredEmail(gate.email || "");
  }, []);

  // if inputs change, reset computed payload (so we don’t email stale results)
  useEffect(() => {
    setResults(null);
    latestPayloadRef.current = { inputs_json: null, results_json: null, pdf_notes: null };
  }, [agi, filingStatusKey, year]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const formatPercentage = (rate) => (rate * 100).toFixed(1) + "%";

  const scrollToResults = () => {
    const el = resultsRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openGateFlow = () => {
    if (unlocked) {
      setShowConfirmModal(true);
      return;
    }
    setShowLeadModal(true);
  };

  const handleLeadSuccess = ({ email }) => {
    writeUnlock({ email });
    setUnlocked(true);
    setStoredEmail(email || "");
    setShowLeadModal(false);
    setShowSuccessModal(true);
  };

  const handleSuccessViewResults = () => {
    setShowSuccessModal(false);
    scrollToResults();
  };

  // ✅ same pattern: compute payload including pdf_notes
  const computePayload = () => {
    const numericAgi = parseFloat(agi);
    if (Number.isNaN(numericAgi) || numericAgi < 0) return null;

    const calculation = calculateTax(numericAgi, filingStatusKey, year);

    // ✅ FIX: inputs_json uses human label + key (best of both worlds)
    const inputs_json = {
      filingStatus: filingStatusLabel, // ✅ "Married Filing Jointly"
      filingStatusKey: filingStatusKey, // optional: keep internal key too
      agi: numericAgi,
      calculator_year: String(year),
    };

    const pdf_notes = {
      disclaimer: yearCfg.disclaimer,
      methods: {
        taxable_income:
          "Taxable income is estimated as AGI minus the standard deduction for your filing status.",
        brackets:
          "Your total tax is estimated by applying the marginal tax rates to portions of taxable income that fall within each bracket.",
      },
    };

    return { inputs_json, results_json: calculation, pdf_notes };
  };

  const runCalculation = () => {
    const payload = computePayload();
    if (!payload) {
      setResults(null);
      latestPayloadRef.current = { inputs_json: null, results_json: null, pdf_notes: null };
      return false;
    }

    setResults(payload.results_json);
    latestPayloadRef.current = payload;
    return true;
  };

  const sendResultsToEmail = async (emailToSend) => {
    const payload = latestPayloadRef.current;
    if (!payload?.inputs_json || !payload?.results_json) return null;

    setSendingResults(true);

    try {
      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/public/send-results",
          method: "POST",
          raw: true,
          qa_mode: qa,
          data: {
            action: "send_results_email",
            email: emailToSend,
            calculator_name: calculatorName,
            calculator_year: String(year),
            page_url: pageUrl,
            timestamp_utc: new Date().toISOString(),
            inputs_json: payload.inputs_json,
            results_json: payload.results_json,
            pdf_notes: payload.pdf_notes,
            schema_version: 1,
          },
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || "Failed to send results email");
      }

      setShowConfirmModal(false);
      setShowUpdateEmailModal(false);
      scrollToResults();

      return resp.data;
    } catch (err) {
      console.error("sendResultsToEmail error:", err);
      return null;
    } finally {
      setSendingResults(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!storedEmail) {
      setShowConfirmModal(false);
      setShowUpdateEmailModal(true);
      return;
    }
    await sendResultsToEmail(storedEmail);
  };

  const handleChangeEmail = () => {
    setShowConfirmModal(false);
    setShowUpdateEmailModal(true);
  };

  const handleUpdateEmailAndSend = async (newEmail) => {
    const e = String(newEmail || "").trim();
    writeUnlock({ email: e });
    setStoredEmail(e);
    setUnlocked(true);
    await sendResultsToEmail(e);
  };

  const canCalculate = (() => {
    const numericAgi = parseFloat(agi);
    return !Number.isNaN(numericAgi) && numericAgi >= 0;
  })();

  return (
    <>
      <div className="">
        <div className="container py-5">
          <div className="row g-5">
            <div className="col-lg-6">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h5 className="fw-bold mb-2 d-flex align-items-center gap-2">
                    <i className="bi bi-currency-dollar text-primary"></i>
                    Tax Information
                  </h5>
                  <p className="text-muted mb-4">
                    Enter your filing status and adjusted gross income to calculate your tax bracket
                  </p>

                  <div className="mb-4">
                    <label className="form-label fw-semibold">Filing Status</label>

                    {/* ✅ FIX: radio values use key, but label shows user text */}
                    {FILING_STATUSES.map((s) => (
                      <div className="form-check" key={s.key}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="filingStatus"
                          id={s.key}
                          value={s.key}
                          checked={filingStatusKey === s.key}
                          onChange={() => setFilingStatusKey(s.key)}
                        />
                        <label className="form-check-label" htmlFor={s.key}>
                          {s.label}
                        </label>
                      </div>
                    ))}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="income" className="form-label fw-semibold">
                      Adjusted Gross Income (AGI)
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-currency-dollar"></i>
                      </span>
                      <input
                        type="number"
                        className="form-control"
                        id="income"
                        placeholder="Enter your AGI"
                        value={agi}
                        onChange={(e) => setAgi(e.target.value)}
                      />
                    </div>
                    <div className="form-text">Your AGI is found on line 11 of your Form 1040</div>
                  </div>

                  <button
                    className="btn w-100"
                    onClick={() => {
                      const ok = runCalculation();
                      if (!ok) return;
                      openGateFlow();
                    }}
                    style={{
                      background: "#0f8a4c",
                      borderColor: "#0f8a4c",
                      color: "white",
                    }}
                    disabled={!canCalculate}
                  >
                    View Results
                  </button>

                  {!canCalculate && (
                    <div className="mt-2 small text-muted">Enter a valid AGI to enable results.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6" ref={resultsRef}>
              <div className="card shadow-sm">
                <div className="card-body">
                  <h5 className="fw-bold d-flex align-items-center gap-2">
                    <i className="bi bi-graph-up text-primary"></i>
                    Your Tax Calculation
                  </h5>
                  <p className="text-muted">Based on {year} federal tax brackets</p>

                  <div
                    className="unk-results-wrap"
                    style={{
                      background: !unlocked
                        ? "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.92))"
                        : "transparent",
                    }}
                  >
                    {!unlocked && <div className="unk-bg-blur" />}

                    <div style={{ position: "relative", zIndex: 1, width: "100%" }}>
                      <div className={!unlocked ? "unk-results-blur" : ""}>
                        {results ? (
                          <>
                            <div className="row g-3 my-4">
                              <div className="col-md-4">
                                <div className="bg-primary text-white rounded p-3">
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <i className="bi bi-percent"></i>
                                    <span className="small">Marginal Rate</span>
                                  </div>
                                  <div className="fs-4 fw-bold">
                                    {formatPercentage(results.marginalRate)}
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-4">
                                <div className="bg-success text-white rounded p-3">
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <i className="bi bi-percent"></i>
                                    <span className="small">Effective Rate</span>
                                  </div>
                                  <div className="fs-4 fw-bold">
                                    {formatPercentage(results.effectiveRate)}
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-4">
                                <div className="bg-danger text-white rounded p-3">
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <i className="bi bi-currency-dollar"></i>
                                    <span className="small">Total Tax</span>
                                  </div>
                                  <div className="fs-4 fw-bold">
                                    {formatCurrency(results.totalTax)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {results.breakdown.length > 0 && (
                              <div className="mt-4">
                                <h6 className="fw-bold mb-3">Tax Breakdown by Bracket</h6>
                                <div className="vstack gap-3">
                                  {results.breakdown.map((bracket, index) => (
                                    <div key={index} className="border rounded p-3 bg-light">
                                      <div className="d-flex justify-content-between fw-semibold text-primary">
                                        <span>{formatPercentage(bracket.rate)} Tax Bracket</span>
                                        <span>{formatCurrency(bracket.tax)}</span>
                                      </div>
                                      <div className="text-muted small">
                                        Income: {formatCurrency(bracket.min)} -{" "}
                                        {bracket.max === Infinity ? "and above" : formatCurrency(bracket.max)}
                                      </div>
                                      <div className="text-muted small">
                                        Taxable in this bracket: {formatCurrency(bracket.taxableAmount)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-muted">
                            Enter your info and click <strong>View Results</strong> to generate your estimate.
                          </div>
                        )}
                      </div>

                      {!unlocked && (
                        <div
                          className="unk-results-overlay"
                          onClick={() => {
                            if (!results) {
                              const ok = runCalculation();
                              if (!ok) return;
                            }
                            openGateFlow();
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              if (!results) {
                                const ok = runCalculation();
                                if (!ok) return;
                              }
                              openGateFlow();
                            }
                          }}
                          aria-label="Results locked. Open unlock modal."
                        >
                          <div className="unk-overlay-card">
                            <div className="unk-overlay-title">Results Locked</div>
                            <div className="unk-overlay-body">
                              Enter your name and email to view your results instantly.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* ✅ Disclaimer from YEAR_CONFIG (same pattern as others) */}
          <div className="text-center text-muted small mt-4 w-50 mx-auto">
            <p>{yearCfg.disclaimer}</p>
          </div>
        </div>
      </div>

      <LeadGateModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSuccess={(payload) => handleLeadSuccess(payload)}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        endpoint={endpoint}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={
          latestPayloadRef.current?.inputs_json || {
            filingStatus: filingStatusLabel,     // ✅ label
            filingStatusKey: filingStatusKey,    // ✅ key (optional)
            agi: parseFloat(agi) || 0,
            calculator_year: String(year),
          }
        }
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onViewResults={handleSuccessViewResults}
      />

      <ConfirmSendResultsModal
        open={showConfirmModal}
        email={storedEmail}
        onClose={() => setShowConfirmModal(false)}
        onViewResults={() => {
          setShowConfirmModal(false);
          scrollToResults();
        }}
        onSend={handleConfirmSend}
        onChangeEmail={handleChangeEmail}
        sending={sendingResults}
      />

      <UpdateEmailModal
        open={showUpdateEmailModal}
        initialEmail={storedEmail}
        onClose={() => setShowUpdateEmailModal(false)}
        onSend={handleUpdateEmailAndSend}
        sending={sendingResults}
        storedEmail={storedEmail}
        endpoint={PROXY_URL}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={latestPayloadRef.current?.inputs_json}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />
    </>
  );
}

export default TaxBracketCalculator;
